import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import { fetchVisitorsByPageGA } from "../services/api";
import Spinner from "./Spinner";


/**
 * VisitorsByPageChart – time‑series line chart
 * Shows page-specific visitor data over time.
 */
const VisitorsByPageChart = ({ startDate, promptId, competitionId }) => {
  const [series, setSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pageFilters = useMemo(() => ([
    `https://writetheworld.org/start-writing/${promptId}`,
    `https://writetheworld.org/competition-page/${competitionId}`,
  ]), [promptId, competitionId]);

  const colors = useMemo(() => ({
    [`https://writetheworld.org/start-writing/${promptId}`]: "#d62728", // red
    [`https://writetheworld.org/competition-page/${competitionId}`]: "#2ca02c", // green
  }), [promptId, competitionId]);

  // Helper: Add/subtract days from YYYY-MM-DD string without timezone issues
  const adjustDate = (dateStr, days) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day, 12, 0, 0); // Local noon
    d.setDate(d.getDate() + days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dy = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dy}`;
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        // Compute GA window: previous month start -> end of startDate month
        const startOfMonth = new Date(`${startDate}T00:00:00`);
        const prevMonthStart = new Date(startOfMonth);
        prevMonthStart.setMonth(prevMonthStart.getMonth() - 1);
        prevMonthStart.setDate(1);
        const monthEnd = new Date(startOfMonth);
        monthEnd.setMonth(monthEnd.getMonth() + 1);
        monthEnd.setDate(0);

        const gaStart = prevMonthStart.toISOString().slice(0, 10);
        const gaEnd = monthEnd.toISOString().slice(0, 10);

        const raw = await fetchVisitorsByPageGA(gaStart, gaEnd);
        // Keep only rows that match one of the competition‑specific pages.
        const filtered = raw.filter((row) =>
          pageFilters.some(
            (page) => row.pageLocation && row.pageLocation.toLowerCase().includes(page.toLowerCase())
          )
        );
        console.log("filtered",filtered);

        // Build a nested lookup: bucket[page][YYYY‑MM‑DD] -> sum(totalUsers)
        const bucket = {};
        filtered.forEach(({ pageLocation, date, totalUsers }) => {
          const key = pageFilters.find((p) => pageLocation.includes(p));
          if (!key) return;

          // Convert "20250805" -> "2025-08-05" as pure string (no timezone issues)
          const isoDate = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;

          if (!bucket[key]) bucket[key] = {};
          bucket[key][isoDate] = (bucket[key][isoDate] || 0) + Number(totalUsers || 0);
        });

        // For each series, add a zero one day BEFORE its first observed point
        pageFilters.forEach((url) => {
          if (!bucket[url]) bucket[url] = {};
          const keys = Object.keys(bucket[url]).sort();
          if (keys.length > 0) {
            const firstKey = keys[0];
            const prevIso = adjustDate(firstKey, -1); // Pure string manipulation, no timezone issues
            if (!bucket[url][prevIso]) bucket[url][prevIso] = 0;

            // Also add a zero one day AFTER its last observed point
            const lastKey = keys[keys.length - 1];
            const nextIso = adjustDate(lastKey, 1); // Pure string manipulation, no timezone issues
            if (!bucket[url][nextIso]) bucket[url][nextIso] = 0;
          }
        });

        // Transform to Nivo series → [{ id, data: [{x, y}], color }]
        const dataset = pageFilters.map((url) => {
          const dataPoints = Object.entries(bucket[url] || {})
            .map(([x, y]) => {
              // Parse date at noon local time to avoid timezone midnight boundary issues
              const [year, month, day] = x.split('-').map(Number);
              const date = new Date(year, month - 1, day, 12, 0, 0);
              return { x: date, y };
            })
            .sort((a, b) => a.x - b.x);

          return {
            id: url.replace("https://writetheworld.org/", "/"),
            data: dataPoints,
            color: colors[url],
          };
        });


        console.log("dataset",dataset);

        setSeries(dataset);
      } catch (err) {
        console.error("Failed to load visitors‑by‑page data", err);
      } finally {
        setIsLoading(false);
      }
    };

    setSeries([]);
    load();
  }, [startDate, promptId, competitionId, pageFilters, colors]);

  return (
    <div className="w-full" style={{ height: 400 }}>
      {isLoading ? (
        <Spinner label="Loading chart…" />
      ) : series.length ? (
        <ResponsiveLine
          data={series}
          margin={{ top: 50, right: 30, bottom: 80, left: 60 }}
          xScale={{ type: "time", format: "native", precision: "day" }}
          xFormat="time:%b %d, %Y"
          yScale={{ type: "linear", min: 0, stacked: false }}
          axisBottom={{
            format: "%b %d, %Y",
            tickValues: "every 2 weeks",
            legend: "Date",
            legendOffset: 50,
            legendPosition: "middle",
            tickRotation: -45,
          }}
          axisLeft={{
            legend: "Total Users",
            legendOffset: -50,
            legendPosition: "middle",
          }}
          colors={({ id }) => series.find((s) => s.id === id)?.color}
          pointSize={4}
          enablePoints={false}
          useMesh={true}
          legends={[
            {
              anchor: "top-left",
              direction: "row",
              translateY: -40,
              itemWidth: 140,
              itemHeight: 14,
              symbolSize: 12,
            },
          ]}
          motionConfig="wobbly"
        />
      ) : (
        <p className="text-center text-sm text-gray-500">No data available.</p>
      )}
    </div>
  );
};

export default VisitorsByPageChart;
